import React from "react";
import { Dialog } from "@material-ui/core";
import { getCurrencySymbol, Icon, LoadingText, PoweredByHopper } from "halifax";
import {
  CardPaymentSelectors,
  RewardsPaymentSelectors,
  useCheckoutStateSelector,
} from "@capone/checkout";
import clsx from "clsx";
import styles from "./styles.module.scss";
import { LOADING_MESSAGES } from "./constants";
import {
  getPackagesShopPricing,
  getQuotedPackageDiscount,
} from "../../state/selectors/common";

interface IMultiMessageLoadingPopupProps {
  open: boolean;
  fullScreen?: boolean;
}

export const MultiMessageLoadingPopup: React.FC<
  IMultiMessageLoadingPopupProps
> = ({ open, fullScreen }) => {
  const [visibleMessageIndex, setVisibleMessageIndex] = React.useState(0);
  const [loadingProgress, setLoadingProgress] = React.useState(0);

  const quotedPackageDiscount = useCheckoutStateSelector(
    getQuotedPackageDiscount
  );
  const packagesShopPricing = useCheckoutStateSelector(getPackagesShopPricing);
  const packageShopDiscount = packagesShopPricing?.totalPackageSavings.fiat;

  const earnForSelectedCard = useCheckoutStateSelector(
    RewardsPaymentSelectors.getEarnForSelectedRewardsAccount
  );
  const earnForHighestEarnAccount = useCheckoutStateSelector(
    RewardsPaymentSelectors.getEarnForHighestEarnRewardsAccount
  );
  const selectedCardPaymentRewardsAccountId = useCheckoutStateSelector(
    CardPaymentSelectors.getSelectedPaymentMethodRewardsAccountId
  );
  const highestEarnRewardsAccount = useCheckoutStateSelector(
    RewardsPaymentSelectors.getHighestEarnAccount
  );

  const rewardsAccountToUse = useCheckoutStateSelector(
    RewardsPaymentSelectors.getRewardsAccounts
  )?.find(
    (account) =>
      account.accountReferenceId ===
      (selectedCardPaymentRewardsAccountId ||
        highestEarnRewardsAccount?.accountReferenceId)
  );

  const earnToUse = earnForSelectedCard || earnForHighestEarnAccount;

  const discount = quotedPackageDiscount
    ? {
        value: quotedPackageDiscount.amount.amount,
        currencyCode: quotedPackageDiscount.amount.currency,
        currencySymbol: getCurrencySymbol(
          quotedPackageDiscount.amount.currency
        ),
      }
    : packageShopDiscount;

  const messagesToUse = React.useMemo(
    () => LOADING_MESSAGES(discount, earnToUse, rewardsAccountToUse),
    [discount, earnToUse, rewardsAccountToUse]
  );

  React.useEffect(() => {
    if (open) {
      const interval = setInterval(() => {
        setVisibleMessageIndex((i) => {
          if (i === messagesToUse.length - 1) {
            return 0;
          }
          return i + 1;
        });
      }, 4000);

      return () => clearInterval(interval);
    }

    setVisibleMessageIndex(0);
    return () => {};
  }, [messagesToUse, open]);

  React.useEffect(() => {
    if (open && loadingProgress === 0) {
      const interval = setInterval(() => {
        setLoadingProgress((oldProgress) => {
          const increment = Math.max(1, 5 - Math.floor(oldProgress / 20)) / 6;
          const newProgress = oldProgress + increment;
          if (newProgress >= 100) {
            clearInterval(interval);
            return 100;
          }
          return newProgress;
        });
      }, 100);
      return () => clearInterval(interval);
    }

    setLoadingProgress(0);

    return () => {};
  }, [open]);

  const messages = React.useMemo(
    () => (
      <div className={styles.messages}>
        {messagesToUse.map((message, index) => (
          <div
            key={message.message.toString()}
            className={clsx(styles["message-content"], {
              [styles.visible]:
                (visibleMessageIndex < 0 ? 0 : visibleMessageIndex) === index,
            })}
          >
            <Icon className={styles.icon} name={message.icon} />
            <LoadingText
              className={styles.text}
              messageId={
                (visibleMessageIndex < 0 ? 0 : visibleMessageIndex) === index
                  ? "visible-package-pq-message"
                  : undefined
              }
              message={message.message}
            />
          </div>
        ))}
      </div>
    ),
    [visibleMessageIndex, messagesToUse]
  );

  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      TransitionProps={{ role: "none" } as never}
      aria-labelledby="visible-package-pq-message"
      className={styles["multi-message-loading-popup"]}
    >
      <div className={styles["loading-content"]}>
        {messages}
        {/* <B2BSpinner className={styles.spinner} /> */}
        <div className={styles["loading-bar-container"]}>
          <div
            className={styles["loading-bar"]}
            style={{ width: `${loadingProgress}%` }}
          />
        </div>
      </div>
      <PoweredByHopper className={styles["powered-by-hopper"]} />
    </Dialog>
  );
};
